import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  useChildBalance,
  useChildStreaks,
  useDayTasks,
  type MemberResponse,
} from '@kidshome/api-contract';
import { Card } from '@kidshome/ui';
import { CoinChip, StarChip, Avatar, ProgressBar } from '@/shared/ui';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { conceptArt } from '@/assets/icons';
import { useFeature } from '@/entities/session';
import { Feature } from '@/shared/lib/features';
import { useHouseholdContext } from '@/shared/lib/household';
import { localDateISO } from '@/shared/lib/dates';
import { isChoreDoneByChild } from '@/shared/lib/chore-status';

/**
 * One child's at-a-glance health snapshot on the family dashboard / overview: avatar + name + their live
 * coin balance (`GET /v1/economy/children/:id/balance`), a per-kid chore-progress bar for today ("2/7
 * chores" from `GET /v1/tasks/day`), and their chore + learning daily streaks (`/economy/.../streaks`,
 * ADR-0080) so a guardian sees all four domains without drilling into each tab. "Done" is the shared
 * kid-facing `isChoreDoneByChild` (approved/skipped/pending_approval) so this matches the child's own
 * "Chores today" stat; a streak chip shows only when its run is live (never a shaming zero — ADR-0028).
 * Only verified data, nothing fabricated. Tapping opens the child's profile.
 */
export function MemberCard({ member }: { member: MemberResponse }): React.ReactElement {
  const { t } = useTranslation('family');
  const { timeZone } = useHouseholdContext();
  const hasFeature = useFeature();
  // Balance/streaks are Economy-module data and the day read is Chores-module data (ADR-0076) — skip
  // the (guaranteed-403) request when the household has turned the backing module off, same idiom as
  // `useInbox`/`PausedRewardsBanner`. The `total > 0` / `balance.data &&` guards below already treat a
  // resolved-empty read as "nothing to show", so a disabled module quietly omits its chips.
  const economyActive = hasFeature(Feature.Economy);
  const choresActive = hasFeature(Feature.Chores);
  const balance = useChildBalance(member.id, { enabled: economyActive });
  const streaks = useChildStreaks(member.id, { enabled: economyActive });
  const today = localDateISO(timeZone);
  const day = useDayTasks(member.id, today, { enabled: choresActive });

  const occurrences = day.data?.groups.flatMap((g) => g.occurrences) ?? [];
  const total = occurrences.length;
  const done = occurrences.filter((o) => isChoreDoneByChild(o.status)).length;
  const percent = total > 0 ? (done / total) * 100 : 0;
  const choreStreak = streaks.data?.chore.current ?? 0;
  const learningStreak = streaks.data?.learning.current ?? 0;
  // The habit streak (aggregate "kept up a habit" activity-day run) now comes from the same economy
  // streaks read as chore + learning (ADR-0080). Its chip shows only while a run is going (never a zero).
  const habitStreak = streaks.data?.habit?.current ?? 0;

  return (
    <Card className="p-3.5">
      <Link
        to={`/family/${member.id}`}
        className="flex items-center gap-3 outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Avatar avatar={member.avatar} size="lg" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="truncate font-semibold text-text">{member.displayName}</span>
          {/* Only show progress once the day read resolves with at least one chore — never a 0/0 bar. */}
          {total > 0 && (
            <div className="flex flex-col gap-1">
              <ProgressBar
                percent={percent}
                label={t('dashboard.choresProgress', { done, total, count: total })}
              />
              <span className="text-xs font-medium text-text-muted">
                {t('dashboard.choresProgress', { done, total, count: total })}
              </span>
            </div>
          )}
          {/* Live chore + learning streaks — per-metric art (broom / flame), each chip shown only while
              its run is going (never a shaming zero). Matches the deck's per-type streak stats. */}
          {(choreStreak > 0 || learningStreak > 0 || habitStreak > 0) && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs font-medium">
              {choreStreak > 0 && (
                <span className="inline-flex items-center gap-1 rounded-pill bg-surface-muted px-2 py-0.5">
                  <ConceptIcon art={conceptArt('broom')} glyph="🧹" size={14} />
                  <span className="font-semibold text-text">{choreStreak}</span>
                  <span className="text-text-muted">{t('streaks.chore')}</span>
                </span>
              )}
              {learningStreak > 0 && (
                <span className="inline-flex items-center gap-1 rounded-pill bg-surface-muted px-2 py-0.5">
                  <ConceptIcon art={conceptArt('flame')} glyph="🔥" size={14} />
                  <span className="font-semibold text-text">{learningStreak}</span>
                  <span className="text-text-muted">{t('streaks.learning')}</span>
                </span>
              )}
              {habitStreak > 0 && (
                <span className="inline-flex items-center gap-1 rounded-pill bg-surface-muted px-2 py-0.5">
                  <ConceptIcon art={conceptArt('sprout')} glyph="🌱" size={14} />
                  <span className="font-semibold text-text">{habitStreak}</span>
                  <span className="text-text-muted">{t('streaks.habit')}</span>
                </span>
              )}
            </div>
          )}
        </div>
        {balance.data && (
          <div className="flex shrink-0 flex-col items-end gap-1">
            {/* Coins (gold spend currency) + effort-stars (✨ violet) — currency separation per the deck.
                The ✨ chip shows only while there's a balance, like the streak chips (never a bare zero). */}
            <CoinChip coins={balance.data.coins} />
            {balance.data.stars > 0 && <StarChip stars={balance.data.stars} size="sm" />}
          </div>
        )}
      </Link>
    </Card>
  );
}
